import { useCallback, useState } from "react";

import { useJoinMeeting } from "~/components/MeetingBanner";
import {
  createInstantMeeting,
  endInstantMeeting,
  InstantMeetingError,
  mintJoinToken,
  type TokenProvider,
} from "~/lib/meet-instant";
import { openMeeting } from "~/lib/meet-link";
import { useMeetingActive } from "~/hooks/useMeetingPresence";
import {
  canSendStateEvent,
  clearMeetingActive,
  ensureMeetingActivePowerLevel,
  getMembers,
  inviteUser,
  MEETING_ACTIVE_STATE_TYPE,
  POWER_LEVELS_EVENT_TYPE,
  setMeetingActive,
  type Client,
  type MeetingPresence,
  type RoomSummary,
} from "~/lib/matrix";
import { deriveSpaceHierarchy, visibleRoomIdsForSpace } from "~/lib/spaces";

export interface UseMeetingActionsParams {
  client: Client | null;
  room: RoomSummary | null;
  userId: string | null;
  openIdProvider: TokenProvider;
  pushToast: (toast: { message: string; tone?: "info" | "danger" | "success" }) => void;
  meetOrigins: readonly string[];
  rooms: RoomSummary[];
}

export function useMeetingActions({
  client,
  room,
  userId,
  openIdProvider,
  pushToast,
  meetOrigins,
  rooms,
}: UseMeetingActionsParams): {
  activeMeeting: MeetingPresence | null;
  canControlMeeting: boolean;
  isHost: boolean;
  busy: boolean;
  start: () => Promise<void>;
  end: () => Promise<void>;
  join: () => Promise<void>;
  joinBusy: boolean;
} {
  const activeMeeting = useMeetingActive(client, room?.id ?? null);
  const canControlMeeting =
    room != null &&
    userId != null &&
    client != null &&
    canSendStateEvent(client, room.id, userId, MEETING_ACTIVE_STATE_TYPE);
  const isHost =
    !!activeMeeting &&
    (activeMeeting.hostUserId === undefined || activeMeeting.hostUserId === userId);
  const [busy, setBusy] = useState(false);
  const joinMeeting = useJoinMeeting(activeMeeting, openIdProvider);

  const start = useCallback(async () => {
    if (busy || !room || !client) return;
    setBusy(true);
    try {
      const orgSpaceId = room.isDirect
        ? undefined
        : deriveSpaceHierarchy(rooms).orgSpaces.find((space) =>
            visibleRoomIdsForSpace(rooms, space.id).has(room.id),
          )?.id;
      const title = room.isDirect ? room.name : `#${room.name}`;
      const result = await createInstantMeeting(room.id, openIdProvider, title, orgSpaceId);
      if (!result.joinCapability) {
        pushToast({ message: "A meeting is already in progress for this room.", tone: "danger" });
        return;
      }
      if (userId && canSendStateEvent(client, room.id, userId, POWER_LEVELS_EVENT_TYPE)) {
        try {
          await ensureMeetingActivePowerLevel(client, room.id);
        } catch (err) {
          console.warn("ensuring meeting power level failed", err);
        }
      }
      await setMeetingActive(client, room.id, {
        meetingId: result.meetingId,
        title: result.title,
        startedAt: result.startedAt,
        joinUrl: result.baseJoinUrl,
        matrixRoomId: room.id,
        joinCapability: result.joinCapability,
        ...(userId ? { hostUserId: userId } : {}),
      });
      if (result.recapBotUserId) {
        const members = getMembers(client, room.id);
        if (!members.some((member) => member.userId === result.recapBotUserId)) {
          try {
            await inviteUser(client, room.id, result.recapBotUserId);
          } catch (err) {
            console.warn("inviting recap bot failed", err);
          }
        }
      }
      const joinUrl = await mintJoinToken(result.meetingId, result.joinCapability, openIdProvider);
      await openMeeting(joinUrl, pushToast, meetOrigins);
    } catch (err) {
      pushToast({ message: startMeetingErrorMessage(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  }, [busy, client, room, rooms, userId, openIdProvider, pushToast, meetOrigins]);

  const end = useCallback(async () => {
    if (busy || !activeMeeting || !room || !client) return;
    setBusy(true);
    try {
      try {
        await endInstantMeeting(activeMeeting.meetingId, openIdProvider);
      } catch (err) {
        if (!(err instanceof InstantMeetingError && err.kind === "not_found")) throw err;
      }
      await clearMeetingActive(client, room.id);
    } catch (err) {
      pushToast({ message: endMeetingErrorMessage(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  }, [busy, activeMeeting, client, room, openIdProvider, pushToast]);

  return {
    activeMeeting,
    canControlMeeting,
    isHost,
    busy,
    start,
    end,
    join: joinMeeting.join,
    joinBusy: joinMeeting.busy,
  };
}

export function startMeetingErrorMessage(err: unknown): string {
  if (err instanceof InstantMeetingError) {
    if (err.kind === "unauthorized") return "Sign in to Nurion to start a meeting.";
    if (err.kind === "forbidden") return "Your Matrix account isn't linked to Nurion yet.";
  }
  return "Could not start the meeting. Try again.";
}

export function endMeetingErrorMessage(err: unknown): string {
  if (err instanceof InstantMeetingError && err.kind === "forbidden") {
    return "Only the person who started the meeting can end it.";
  }
  return "Could not end the meeting. Try again.";
}
